import { useReactiveVar } from "@/hooks/useVar.tsx";
import { useCallback, useState } from "react";
import { styled } from "styled-components";
import { showSettingsVar } from "@/vars/showSettings.ts";
import { HStack, VStack } from "@/components/layout/Layout.tsx";
import { Dialog } from "@/components/layout/Dialog.tsx";
import { Button } from "@/components/forms/Button.tsx";
import { ToggleButton, ToggleGroup } from "@/components/forms/ToggleGroup.tsx";
import {
  type Preset,
  presets,
  shortcutSettingsVar,
} from "@/vars/shortcutSettings.ts";
import { presetOverrides } from "@/vars/presets.ts";
import { gameplaySettingsVar } from "@/vars/gameplaySettings.ts";
import { Gameplay } from "./Gameplay.tsx";
import { Shortcuts } from "./Shortcuts/Shortcuts.tsx";
import { Audio } from "./Audio.tsx";
import { Maps } from "./Maps.tsx";
import { UI } from "./UI.tsx";

const SettingsDialog = styled(Dialog)`
  width: 700px;
  max-width: calc(100% - 16px);
  height: 80vh;
  max-height: 800px;
  gap: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  padding-right: 0;
  padding-left: 0;
`;

const SettingsContent = styled(HStack)`
  flex: 1;
  overflow: hidden;
  align-items: stretch;
  gap: 0;
`;

const TabsContainer = styled(VStack)`
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  padding-right: ${({ theme }) => theme.spacing.lg};
  margin-left: ${({ theme }) => theme.spacing.lg};
  min-width: 120px;
`;

const Tab = styled(Button)<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : "transparent"};
  color: ${({ theme }) => theme.colors.body};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) =>
    theme.spacing.lg};
  width: 100%;
  text-align: left;
  outline: none;

  &.hover:not([disabled]) {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const TabContent = styled.div`
  flex: 1;
  overflow: hidden;
  padding-left: ${({ theme }) => theme.spacing.lg};
`;

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Header = styled.h1`
  margin-left: ${({ theme }) => theme.spacing.lg};
`;

const CloseButton = styled(Button)`
  padding: 0 ${({ theme }) => theme.spacing.md};
  margin-left: auto;
  margin-right: ${({ theme }) => theme.spacing.lg};
  font-weight: 800;
`;

export const Settings = () => {
  const showSettings = useReactiveVar(showSettingsVar);
  const { preset } = useReactiveVar(shortcutSettingsVar);
  const [activeTab, setActiveTab] = useState<
    "gameplay" | "shortcuts" | "audio" | "maps" | "ui"
  >(
    "gameplay",
  );
  const setPreset = useCallback(
    (p: Preset) => {
      const overrides = presetOverrides[p];
      shortcutSettingsVar({
        ...shortcutSettingsVar(),
        preset: p,
        ...(overrides.useSlotBindings != null
          ? { useSlotBindings: overrides.useSlotBindings }
          : {}),
      });
      if (overrides.clearOrderOnRightClick != null) {
        gameplaySettingsVar({
          ...gameplaySettingsVar(),
          clearOrderOnRightClick: overrides.clearOrderOnRightClick,
        });
      }
    },
    [],
  );

  if (!showSettings) return null;

  return (
    <SettingsDialog>
      <SettingsHeader>
        <Header>Settings</Header>
        <ToggleGroup>
          {presets.map((p) => (
            <ToggleButton
              key={p}
              type="button"
              $active={preset === p}
              onClick={() => setPreset(p)}
            >
              {p.toUpperCase()}
            </ToggleButton>
          ))}
        </ToggleGroup>
        <CloseButton
          type="button"
          onClick={() => showSettingsVar(false)}
          title="Close settings"
        >
          ✕
        </CloseButton>
      </SettingsHeader>
      <SettingsContent>
        <TabsContainer>
          <Tab
            $active={activeTab === "gameplay"}
            onClick={() => setActiveTab("gameplay")}
          >
            Gameplay
          </Tab>
          <Tab
            $active={activeTab === "shortcuts"}
            onClick={() => setActiveTab("shortcuts")}
          >
            Shortcuts
          </Tab>
          <Tab
            $active={activeTab === "audio"}
            onClick={() => setActiveTab("audio")}
          >
            Audio
          </Tab>
          <Tab
            $active={activeTab === "maps"}
            onClick={() => setActiveTab("maps")}
          >
            Maps
          </Tab>
          <Tab
            $active={activeTab === "ui"}
            onClick={() => setActiveTab("ui")}
          >
            UI
          </Tab>
        </TabsContainer>
        <TabContent>
          {activeTab === "gameplay" && <Gameplay />}
          {activeTab === "shortcuts" && <Shortcuts />}
          {activeTab === "audio" && <Audio />}
          {activeTab === "maps" && <Maps />}
          {activeTab === "ui" && <UI />}
        </TabContent>
      </SettingsContent>
    </SettingsDialog>
  );
};
